import { Collapse, Divider, Image, Select } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '../../../hooks/redux';
import {
    getRatingSystems,
    getAllEpdsFiltersAction,
    getSmEpdsFiltersAction,
    getEc3FiltersAction,
    getRegulationsFiltersAction,
    getMaterialsFiltersAction,
    getLowEmittingFiltersAction,
} from '../../../redux/actions/transparencyCatalogActions';
import { CloseIcon, PrimaryButton } from '../../../styles/common.styles';
import TagsPicker from '../../tags/TagsPicker/TagsPicker';
import { Tag as TagType } from '../../../redux/actions/tagActions/types';
import {
    ButtonsGroup,
    CheckboxWrapper,
    ClearAllButton,
    CloseButton,
    CollapseWrapper,
    SubCheckboxWrapper,
    DrawerHeader,
    DrawerTitle,
    DrawerWrapper,
    FilterSubcategoryTitle,
    ProductsFiltersGroup,
    FilterTitleWrapper,
    FilterImageWrapper,
    FiltersTipContainer,
    HPDTitleWrapper,
    ImpactAreaIconWrapper,
    ImpactAreaIconTooltip,
    CollapseHeaderText,
    LowEmmittingHeaderWrapper,
    LowEmmittingCollapseSubHeaderText,
    FilterContainer,
    FilterTitle,
} from './ProductsFilterModal.styles';
import Switcher from '../../common/switcher/Switcher';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { ProductsFilterData } from '../../../redux/actions/transparencyCatalogActions/types';
import _ from 'lodash';
import { getPercentileColor } from '../../favoritProducts/favoriteProductItem/productItemHeader/ProductItemHeaderHelper';
import { EC3EmbodiedCarbon } from '../../../models/product/enums';
import { EC3Rect } from '../../common/ec3/EC3Rect.styles';
import EC3Tip from '../../favoritProducts/favoriteProductItem/productItemHeader/tips/ec3Tip/EC3Tip';
import HpdTip from '../../favoritProducts/favoriteProductItem/productItemHeader/tips/materialPrograms/hpdTip/HpdTip';
import { blueLink } from '../../../styles/colors';
const { Option } = Select;

type Props = {
    open: boolean;
    onCancel: () => void;
    onFilter?: (filters: ProductFilters) => void;
    type?: 'guideline' | 'project';
};

export type ProductFilters = {
    ratingSystems: ProductsFilterData[];
    epdFilters: ProductsFilterData[];
    smEpdsFilters: ProductsFilterData[];
    ec3Filters: ProductsFilterData[];
    regulationsFilters: ProductsFilterData[];
    materialsFilters: ProductsFilterData[];
    lowEmittingFilters: ProductsFilterData[];
    lemRegulationsFilters: ProductsFilterData[];
    tags: TagType[];
    dateFilters?: ProductDateFilter[];
};

export type ProductDateFilter = 'last7Days' | 'last30Days' | 'last6Months' | 'lastYear';

const LEM_REGULATIONS_FILTERS: ProductsFilterData[] = [
    { id: 'hasCdphCompliant', title: 'CDPH compliant' },
];

const ProductsFilterModal = ({
    open,
    onCancel = () => {},
    onFilter = () => {},
    type = 'project',
}: Props) => {
    const dispatch = useAppDispatch();

    const [ratingSystems, setRatingSystems] = useState<ProductsFilterData[]>([]);
    const [optimizationRatingSystems, setOptimizationRatingSystems] = useState<
        ProductsFilterData[]
    >([]);
    const [epdFilters, setEpdFilters] = useState<ProductsFilterData[]>([]);
    const [smEpdsFilters, setSmEpdsFilters] = useState<ProductsFilterData[]>([]);
    const [ec3Filters, setEc3Filters] = useState<ProductsFilterData[]>([]);
    const [regulationsFilters, setRegulationsFilters] = useState<ProductsFilterData[]>([]);
    const [materialsFilters, setMaterialsFilters] = useState<ProductsFilterData[]>([]);
    const [lowEmittingFilters, setlowEmittingFilters] = useState<ProductsFilterData[]>([]);

    const [tooltipTranslation, setTooltipTranslation] = useState<{ y: number }>({ y: 0 });

    const [filters, setFilters] = useState<ProductFilters>({
        ratingSystems: [],
        epdFilters: [],
        smEpdsFilters: [],
        ec3Filters: [],
        regulationsFilters: [],
        materialsFilters: [],
        lowEmittingFilters: [],
        lemRegulationsFilters: [],
        tags: [],
        dateFilters: [],
    });
    const [submittedFilters, setSubmittedFilters] = useState<ProductFilters>({
        ratingSystems: [],
        epdFilters: [],
        smEpdsFilters: [],
        ec3Filters: [],
        regulationsFilters: [],
        materialsFilters: [],
        lowEmittingFilters: [],
        lemRegulationsFilters: [],
        tags: [],
        dateFilters: [],
    });

    useEffect(() => {
        if (!open) {
            return;
        }

        dispatch(getRatingSystems())
            .unwrap()
            .then((result) => {
                const subTitle = 'optimization';
                setRatingSystems(
                    result.data.filter((system) => !system.title.toLowerCase().includes(subTitle)),
                );
                setOptimizationRatingSystems(
                    result.data.filter((system) => system.title.toLowerCase().includes(subTitle)),
                );
            });

        dispatch(getAllEpdsFiltersAction())
            .unwrap()
            .then((result) => {
                setEpdFilters(result.data);
            });

        dispatch(getSmEpdsFiltersAction())
            .unwrap()
            .then((result) => {
                setSmEpdsFilters(result.data);
            });

        dispatch(getEc3FiltersAction())
            .unwrap()
            .then((result) => {
                setEc3Filters(result.data);
            });

        dispatch(getRegulationsFiltersAction())
            .unwrap()
            .then((result) => {
                setRegulationsFilters(result.data);
            });

        dispatch(getMaterialsFiltersAction())
            .unwrap()
            .then((result) => {
                setMaterialsFilters(result.data);
            });

        dispatch(getLowEmittingFiltersAction())
            .unwrap()
            .then((result) => {
                setlowEmittingFilters(result.data);
            });
        setFilters(JSON.parse(JSON.stringify(submittedFilters)));
        setFilters(JSON.parse(JSON.stringify(submittedFilters)));
    }, [open]);

    const onFilterCheck = (
        e: CheckboxChangeEvent,
        filter: ProductsFilterData,
        filterType: keyof ProductFilters,
    ) => {
        setFilters((prev) => {
            const updatedFilter = e.target.checked
                ? [...prev[filterType], filter]
                : prev[filterType].filter((f) => f.id !== filter.id);

            return { ...prev, [filterType]: updatedFilter };
        });
    };

    const onMaterialFilterCheck = (e: CheckboxChangeEvent, filter: ProductsFilterData) => {
        const isChecked = e.target.checked;
        const filtersToUpdate = [filter, ...(filter.descendants || [])];

        setFilters((prev) => {
            const updatedMaterialsFilters = isChecked
                ? [...prev.materialsFilters, ...filtersToUpdate]
                : prev.materialsFilters.filter(
                      (f) => !filtersToUpdate.some((toRemove) => toRemove.id === f.id),
                  );

            return { ...prev, materialsFilters: updatedMaterialsFilters };
        });
    };

    const handleFilterClick = (filtersToApply: ProductFilters) => {
        onFilter(filtersToApply);
        setSubmittedFilters(JSON.parse(JSON.stringify(filtersToApply)));
    };

    const isAnySelected = useMemo(() => {
        return Object.values(filters).some((filterArray) => filterArray.length > 0);
    }, [filters]);

    const isFilterButtonDisabled = useMemo(() => {
        const normalize = (productFilters: ProductFilters) => {
            return {
                ratingSystems: [...productFilters.ratingSystems].sort((a, b) =>
                    a.title.localeCompare(b.title),
                ),
                epdFilters: [...productFilters.epdFilters].sort((a, b) =>
                    a.title.localeCompare(b.title),
                ),
                smEpdsFilters: [...productFilters.smEpdsFilters].sort((a, b) =>
                    a.title.localeCompare(b.title),
                ),
                ec3Filters: [...productFilters.ec3Filters].sort((a, b) =>
                    a.title.localeCompare(b.title),
                ),
                regulationsFilters: [...productFilters.regulationsFilters].sort((a, b) =>
                    a.title.localeCompare(b.title),
                ),
                materialsFilters: [...productFilters.materialsFilters].sort((a, b) =>
                    a.title.localeCompare(b.title),
                ),
                lowEmittingFilters: [...productFilters.lowEmittingFilters].sort((a, b) =>
                    a.title.localeCompare(b.title),
                ),
                lemRegulationsFilters: [...(productFilters.lemRegulationsFilters || [])].sort(
                    (a, b) => a.title.localeCompare(b.title),
                ),
                tags: [...(productFilters.tags || [])].sort((a, b) => (a.id ?? 0) - (b.id ?? 0)),
                dateFilters: [...(productFilters.dateFilters || [])].sort(),
            };
        };

        return _.isEqual(normalize(submittedFilters), normalize(filters));
    }, [submittedFilters, filters]);

    const clearFilters = () => {
        const clearedFilters: ProductFilters = {
            ratingSystems: [],
            epdFilters: [],
            smEpdsFilters: [],
            ec3Filters: [],
            regulationsFilters: [],
            materialsFilters: [],
            lowEmittingFilters: [],
            lemRegulationsFilters: [],
            tags: [],
            dateFilters: [],
        };

        setFilters(clearedFilters);
        handleFilterClick(clearedFilters);
    };

    const getInspectAreaFilterImages = (filterTitle: string) => {
        const climate = {
            url: 'src/assets/images/climate_blue_notext.svg',
            title: 'Climate Health',
        };
        const circularity = {
            url: 'src/assets/images/circularity_blue_notext.svg',
            title: 'Circular Economy',
        };
        const humanHealth = {
            url: 'src/assets/images/health_red_notext.svg',
            title: 'Human Health',
        };
        const socialHealth = {
            url: 'src/assets/images/social_purple_notext.svg',
            title: 'Social Health & Equity',
        };
        const ecosystemHealth = {
            url: 'src/assets/images/ecosystem_green_notext.svg',
            title: 'Ecosystem Health',
        };

        const imageUrls = [];
        if (
            filterTitle.toLowerCase() === 'All EPDs'.toLowerCase() ||
            filterTitle.toLowerCase() === 'SM EDPs'.toLowerCase()
        ) {
            imageUrls.push(climate, circularity);
        }

        if (
            filterTitle.toLowerCase().includes('Health Product'.toLowerCase()) ||
            filterTitle.toLowerCase().includes('Declare'.toLowerCase()) ||
            filterTitle.toLowerCase().includes('C2C Material Health'.toLowerCase()) ||
            filterTitle.toLowerCase().includes('Low-emitting certificates'.toLowerCase())
        ) {
            imageUrls.push(humanHealth);
        }

        if (
            filterTitle.toLowerCase().includes('C2C Certified'.toLowerCase()) ||
            filterTitle.toLowerCase().includes('Living Product'.toLowerCase())
        ) {
            imageUrls.push(humanHealth);
            imageUrls.push(climate);
            imageUrls.push(circularity);
            imageUrls.push(socialHealth);
            imageUrls.push(ecosystemHealth);
        }

        return imageUrls;
    };

    const handleTooltipMouseEnter = () => {
        const drawer = document.getElementsByClassName('ant-drawer-body');

        if (drawer) {
            setTooltipTranslation({
                y: -drawer[0].scrollTop,
            });
        }
    };

    const lowEmittingCollapseHeader = (
        <LowEmmittingHeaderWrapper>
            <CollapseHeaderText>
                <span style={{color: '#f26522'}}>New!</span> Low-emitting types
            </CollapseHeaderText>
            <LowEmmittingCollapseSubHeaderText>
                More content coming Aug-Oct
            </LowEmmittingCollapseSubHeaderText>
        </LowEmmittingHeaderWrapper>
    );

    const dateOptions: { key: ProductDateFilter | 'anytime'; label: string }[] = [
        { key: 'anytime', label: 'Anytime' },
        { key: 'last7Days', label: 'Last 7 days' },
        { key: 'last30Days', label: 'Last 30 days' },
        { key: 'last6Months', label: 'Last 6 months' },
        { key: 'lastYear', label: 'Last year' },
    ];

    return (
        <DrawerWrapper
            open={open}
            onClose={onCancel}
            width={419}
            height={'100%'}
            placement="left"
            getContainer={false}
            headerStyle={{ display: 'none' }}
            mask={false}
        >
            <DrawerHeader>
                <Image src="src/assets/images/filter.svg" preview={false} />
                <DrawerTitle>Filter</DrawerTitle>

                <CloseButton onClick={onCancel}>
                    <CloseIcon />
                </CloseButton>
            </DrawerHeader>

            <ButtonsGroup>
                {isAnySelected ? (
                    <ClearAllButton type="link" onClick={clearFilters}>
                        Clear all
                    </ClearAllButton>
                ) : null}
                <PrimaryButton
                    disabled={isFilterButtonDisabled}
                    onClick={() => handleFilterClick(filters)}
                >
                    Filter
                </PrimaryButton>
            </ButtonsGroup>

            <Divider style={{ margin: '24px 0 0 0' }} />

            <CollapseWrapper
                expandIcon={(props) => (
                    <Switcher
                        width={20}
                        height={20}
                        clicked={!!props.isActive}
                        stopPropagation={false}
                    />
                )}
            >
                <Collapse.Panel header="Rating systems" key="1">
                    <ProductsFiltersGroup>
                        {ratingSystems.map((system) => (
                            <CheckboxWrapper
                                key={system.id}
                                checked={filters.ratingSystems.some((f) => f.id === system.id)}
                                onChange={(e) => onFilterCheck(e, system, 'ratingSystems')}
                            >
                                {system.title}
                            </CheckboxWrapper>
                        ))}
                    </ProductsFiltersGroup>
                    <FilterTitleWrapper>
                        <FilterSubcategoryTitle>Optimization</FilterSubcategoryTitle>
                    </FilterTitleWrapper>
                    <ProductsFiltersGroup>
                        {optimizationRatingSystems.map((system) => (
                            <CheckboxWrapper
                                key={system.id}
                                checked={filters.ratingSystems.some((f) => f.id === system.id)}
                                onChange={(e) => onFilterCheck(e, system, 'ratingSystems')}
                            >
                                {system.title}
                            </CheckboxWrapper>
                        ))}
                        <a
                            href="https://transparencycatalog.com/rating-systems"
                            style={{ marginTop: 3, color: blueLink }}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Check disclosure credit eligibility
                        </a>
                    </ProductsFiltersGroup>
                </Collapse.Panel>
                <Divider />
                <Collapse.Panel header="EPD types / embodied carbon" key="2">
                    <FilterTitleWrapper>
                        <FilterSubcategoryTitle>All EPDs</FilterSubcategoryTitle>
                        <FilterImageWrapper>
                            {getInspectAreaFilterImages('All EPDs').map((image, index) => (
                                <ImpactAreaIconWrapper key={index}>
                                    <Image
                                        preview={false}
                                        src={image.url}
                                        title={image.title}
                                        width={14}
                                        height={14}
                                        style={{ verticalAlign: 'top' }}
                                        onMouseEnter={handleTooltipMouseEnter}
                                    />
                                    <ImpactAreaIconTooltip
                                        className="impact-area-tooltip"
                                        yTranslation={tooltipTranslation.y + 'px'}
                                    >
                                        {image.title}
                                    </ImpactAreaIconTooltip>
                                </ImpactAreaIconWrapper>
                            ))}
                        </FilterImageWrapper>
                    </FilterTitleWrapper>
                    <ProductsFiltersGroup>
                        {epdFilters.map((filter, index) => (
                            <CheckboxWrapper
                                key={index}
                                checked={filters.epdFilters.some((f) => f.id === filter.id)}
                                onChange={(e) => onFilterCheck(e, filter, 'epdFilters')}
                            >
                                {filter.title}
                            </CheckboxWrapper>
                        ))}
                    </ProductsFiltersGroup>
                    <FilterTitleWrapper>
                        <FilterSubcategoryTitle>
                            Sustainable Minds <br /> Transparency Reports [EPD]&trade;
                        </FilterSubcategoryTitle>
                        <FilterImageWrapper>
                            {getInspectAreaFilterImages('SM EDPs').map((image, index) => (
                                <ImpactAreaIconWrapper key={index}>
                                    <Image
                                        preview={false}
                                        src={image.url}
                                        title={image.title}
                                        width={14}
                                        height={14}
                                        style={{ verticalAlign: 'top' }}
                                        onMouseEnter={handleTooltipMouseEnter}
                                    />
                                    <ImpactAreaIconTooltip
                                        className="impact-area-tooltip"
                                        yTranslation={tooltipTranslation.y + 'px'}
                                    >
                                        {image.title}
                                    </ImpactAreaIconTooltip>
                                </ImpactAreaIconWrapper>
                            ))}
                        </FilterImageWrapper>
                    </FilterTitleWrapper>
                    <ProductsFiltersGroup>
                        {smEpdsFilters.map((filter, index) => (
                            <CheckboxWrapper
                                key={index}
                                checked={filters.smEpdsFilters.some((f) => f.id === filter.id)}
                                onChange={(e) => onFilterCheck(e, filter, 'smEpdsFilters')}
                            >
                                {filter.title}
                            </CheckboxWrapper>
                        ))}
                    </ProductsFiltersGroup>
                    <FilterTitleWrapper>
                        <FilterSubcategoryTitle>
                            EC3 embodied carbon
                            <FiltersTipContainer
                                onMouseEnter={handleTooltipMouseEnter}
                                yTranslation={tooltipTranslation.y + 'px'}
                            >
                                <EC3Tip />
                            </FiltersTipContainer>
                        </FilterSubcategoryTitle>
                    </FilterTitleWrapper>
                    <ProductsFiltersGroup>
                        {ec3Filters.map((filter, index) => (
                            <CheckboxWrapper
                                key={index}
                                checked={filters.ec3Filters.some((f) => f.id === filter.id)}
                                onChange={(e) => onFilterCheck(e, filter, 'ec3Filters')}
                            >
                                <EC3Rect
                                    color={getPercentileColor(filter.title as EC3EmbodiedCarbon)}
                                />
                                {filter.title}
                            </CheckboxWrapper>
                        ))}
                    </ProductsFiltersGroup>
                    <FilterTitleWrapper>
                        <FilterSubcategoryTitle>Regulations</FilterSubcategoryTitle>
                    </FilterTitleWrapper>
                    <ProductsFiltersGroup>
                        {regulationsFilters.map((filter, index) => (
                            <CheckboxWrapper
                                key={index}
                                checked={filters.regulationsFilters.some((f) => f.id === filter.id)}
                                onChange={(e) => onFilterCheck(e, filter, 'regulationsFilters')}
                            >
                                {filter.title}
                            </CheckboxWrapper>
                        ))}
                    </ProductsFiltersGroup>
                </Collapse.Panel>
                <Divider />
                <Collapse.Panel header="Material disclosure types" key="3">
                    <ProductsFiltersGroup>
                        {materialsFilters.map((filter, index) => (
                            <React.Fragment key={index}>
                                <FilterTitleWrapper style={{ marginTop: '0px' }}>
                                    <CheckboxWrapper
                                        checked={filters.materialsFilters.some(
                                            (f) => f.id === filter.id,
                                        )}
                                        onChange={(e) => onMaterialFilterCheck(e, filter)}
                                    >
                                        <HPDTitleWrapper>
                                            <span
                                                dangerouslySetInnerHTML={{ __html: filter.title }}
                                            ></span>
                                            {filter.title.includes('HPD') && (
                                                <FiltersTipContainer
                                                    onMouseEnter={handleTooltipMouseEnter}
                                                    yTranslation={tooltipTranslation.y + 'px'}
                                                    className="hpd-tooltip-container"
                                                >
                                                    <HpdTip />
                                                </FiltersTipContainer>
                                            )}
                                        </HPDTitleWrapper>
                                    </CheckboxWrapper>
                                    <FilterImageWrapper>
                                        {getInspectAreaFilterImages(filter.title).map(
                                            (image, imageIndex) => (
                                                <ImpactAreaIconWrapper key={imageIndex}>
                                                    <Image
                                                        preview={false}
                                                        src={image.url}
                                                        title={image.title}
                                                        width={14}
                                                        height={14}
                                                        style={{ marginTop: '4px' }}
                                                        onMouseEnter={handleTooltipMouseEnter}
                                                    />
                                                    <ImpactAreaIconTooltip
                                                        className="impact-area-tooltip"
                                                        yTranslation={tooltipTranslation.y + 'px'}
                                                    >
                                                        {image.title}
                                                    </ImpactAreaIconTooltip>
                                                </ImpactAreaIconWrapper>
                                            ),
                                        )}
                                    </FilterImageWrapper>
                                </FilterTitleWrapper>
                                {filter.descendants &&
                                    filter.descendants.length > 0 &&
                                    filter.descendants.map((descendant: ProductsFilterData) => (
                                        <SubCheckboxWrapper
                                            key={descendant.id}
                                            checked={filters.materialsFilters.some(
                                                (f) => f.id === descendant.id,
                                            )}
                                            onChange={(e) =>
                                                onFilterCheck(e, descendant, 'materialsFilters')
                                            }
                                        >
                                            {descendant.title}
                                        </SubCheckboxWrapper>
                                    ))}
                            </React.Fragment>
                        ))}
                    </ProductsFiltersGroup>
                </Collapse.Panel>
                <Divider />
                <Collapse.Panel header={lowEmittingCollapseHeader} key="4">
                    <ProductsFiltersGroup>
                        {(() => {
                            const isAllLowEmittingSelected =
                                lowEmittingFilters.length > 0 &&
                                lowEmittingFilters.every((le) =>
                                    filters.lowEmittingFilters.some((f) => f.id === le.id),
                                );
                            return (
                                <FilterTitleWrapper style={{ marginTop: '0px' }}>
                                    <CheckboxWrapper
                                        checked={isAllLowEmittingSelected}
                                        indeterminate={
                                            filters.lowEmittingFilters.length > 0 &&
                                            !isAllLowEmittingSelected
                                        }
                                        onChange={(e) =>
                                            setFilters((prev) => ({
                                                ...prev,
                                                lowEmittingFilters: e.target.checked
                                                    ? [...lowEmittingFilters]
                                                    : [],
                                            }))
                                        }
                                    >
                                        Low-emitting certificates
                                    </CheckboxWrapper>
                                    <FilterImageWrapper>
                                        {getInspectAreaFilterImages(
                                            'Low-emitting certificates',
                                        ).map((image, imageIndex) => (
                                            <ImpactAreaIconWrapper key={imageIndex}>
                                                <Image
                                                    preview={false}
                                                    src={image.url}
                                                    title={image.title}
                                                    width={14}
                                                    height={14}
                                                    style={{ marginTop: '4px' }}
                                                    onMouseEnter={handleTooltipMouseEnter}
                                                />
                                                <ImpactAreaIconTooltip
                                                    className="impact-area-tooltip"
                                                    yTranslation={tooltipTranslation.y + 'px'}
                                                >
                                                    {image.title}
                                                </ImpactAreaIconTooltip>
                                            </ImpactAreaIconWrapper>
                                        ))}
                                    </FilterImageWrapper>
                                </FilterTitleWrapper>
                            );
                        })()}

                        {lowEmittingFilters.map((filter, index) => (
                            <FilterTitleWrapper style={{ marginTop: 0 }} key={index}>
                                <SubCheckboxWrapper
                                    checked={filters.lowEmittingFilters.some(
                                        (f) => f.id === filter.id,
                                    )}
                                    onChange={(e) => onFilterCheck(e, filter, 'lowEmittingFilters')}
                                >
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: `${filter?.certifier ?? ''} ${filter.title}`,
                                        }}
                                    ></span>
                                </SubCheckboxWrapper>
                            </FilterTitleWrapper>
                        ))}
                    </ProductsFiltersGroup>
                    <FilterTitleWrapper>
                        <FilterSubcategoryTitle>Regulations</FilterSubcategoryTitle>
                    </FilterTitleWrapper>
                    <ProductsFiltersGroup>
                        {LEM_REGULATIONS_FILTERS.map((filter) => (
                            <CheckboxWrapper
                                key={filter.id}
                                checked={filters.lemRegulationsFilters.some(
                                    (f) => f.id === filter.id,
                                )}
                                onChange={(e) => onFilterCheck(e, filter, 'lemRegulationsFilters')}
                            >
                                {filter.title}
                            </CheckboxWrapper>
                        ))}
                    </ProductsFiltersGroup>
                </Collapse.Panel>
                <Divider />
                <Collapse.Panel header="Tags" key="5">
                    <div style={{ paddingTop: 8 }}>
                        <TagsPicker
                            selectedTags={filters?.tags}
                            setSelectedTags={(tags) => setFilters((prev) => ({ ...prev, tags }))}
                        />
                    </div>
                </Collapse.Panel>
                {type === 'guideline' && (
                    <Collapse.Panel header="Date added" key="6">
                        <FilterContainer>
                            <FilterTitle>Show products added</FilterTitle>
                            <Select
                                aria-required
                                onChange={(key) => {
                                    const selectedOption = dateOptions.find(
                                        (option) => option.key === key,
                                    );
                                    if (!selectedOption) {
                                        return;
                                    }

                                    setFilters((prev) => ({
                                        ...prev,
                                        dateFilters:
                                            selectedOption.key === 'anytime'
                                                ? []
                                                : [selectedOption.key],
                                    }));
                                }}
                                value={filters.dateFilters?.[0] ?? 'anytime'}
                                suffixIcon={
                                    <Image
                                        src="src/assets/images/ic-dropdown-arrow.svg"
                                        preview={false}
                                    />
                                }
                            >
                                {dateOptions
                                    ? dateOptions.map((item) => (
                                          <Option
                                              style={{ fontSize: 12 }}
                                              value={item.key}
                                              key={item.key}
                                          >
                                              {item.label}
                                          </Option>
                                      ))
                                    : null}
                            </Select>
                        </FilterContainer>
                    </Collapse.Panel>
                )}
            </CollapseWrapper>
        </DrawerWrapper>
    );
};

export default ProductsFilterModal;
